import { useCallback, useState } from 'react';

import { useWallContentStore, type WallBoard } from '@walls/store';
import { getExportFilename } from './exportFilenames';
import { captureWallExport, getCanvasImageData } from './wallExportCapture';

export function useExportWallPdf(board: WallBoard) {
  const [isExporting, setIsExporting] = useState(false);
  const cards = useWallContentStore((state) => state.cards);
  const connections = useWallContentStore((state) => state.connections);
  const shapes = useWallContentStore((state) => state.shapes);

  const exportWallPdf = useCallback(async () => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);

    try {
      const [{ jsPDF }, { bounds, canvas }] = await Promise.all([
        import('jspdf'),
        captureWallExport({
          board,
          cards,
          connections,
          shapes,
        }),
      ]);
      const imageData = getCanvasImageData(canvas);
      const pdf = new jsPDF({
        format: 'a4',
        orientation: bounds.width > bounds.height ? 'landscape' : 'portrait',
        unit: 'px',
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 5;
      const maxWidth = pageWidth - margin * 2;
      const maxHeight = pageHeight - margin * 2;
      const ratio = Math.min(maxWidth / bounds.width, maxHeight / bounds.height, 1);
      const imageWidth = bounds.width * ratio;
      const imageHeight = bounds.height * ratio;

      pdf.addImage(imageData.dataUrl, imageData.format, margin, margin, imageWidth, imageHeight);
      pdf.save(getExportFilename(board.title, 'pdf'));
    } finally {
      setIsExporting(false);
    }
  }, [board, cards, connections, isExporting, shapes]);

  return { exportWallPdf, isExporting };
}
